import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, FolderOpen, Check, ArrowRight, ArrowLeft,
  Upload, Sparkles, Shield, ChevronRight, AlertCircle, 
  Download, FolderPlus, FileText, Chrome, Mail, Eye, EyeOff, Loader2, Monitor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Folder structure for JaySmart
const FOLDER_STRUCTURE = [
  { name: 'Rechnungen', color: 'blue', description: 'Alle eingehenden Rechnungen' },
  { name: 'Behörden', color: 'red', description: 'Finanzamt, Gemeinde, etc.' },
  { name: 'Versicherungen', color: 'emerald', description: 'Alle Versicherungsdokumente' },
  { name: 'Miete', color: 'amber', description: 'Mietverträge, Nebenkostenabrechnungen' },
  { name: 'Verträge', color: 'violet', description: 'Arbeitsverträge, Abos, etc.' },
  { name: 'Gehalt', color: 'pink', description: 'Gehaltsabrechnungen' },
  { name: 'Bank', color: 'cyan', description: 'Kontoauszüge, Kreditverträge' },
  { name: 'Gesundheit', color: 'rose', description: 'Arztrechnungen, Krankenkasse' },
  { name: 'Kfz', color: 'orange', description: 'Auto, Werkstatt, TÜV' },
  { name: 'Sonstiges', color: 'slate', description: 'Alle anderen Dokumente' }
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { token, updateUser, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [folderHandle, setFolderHandle] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [companyCreated, setCompanyCreated] = useState(false);
  const [browserSupported, setBrowserSupported] = useState(true);
  const [zipDownloaded, setZipDownloaded] = useState(false);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    smtp_server: 'smtp.office365.com',
    smtp_port: '587',
    smtp_email: '',
    smtp_password: '',
    sender_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  
  const [companyData, setCompanyData] = useState({
    name: '',
    legal_form: '',
    address: '',
    city: '',
    postal_code: '',
    industry: '',
    vat_id: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (!('showDirectoryPicker' in window)) {
      setBrowserSupported(false);
    }
  }, [token, navigate]);

  const handleCompanySubmit = async () => {
    if (!companyData.name) {
      toast.error('Bitte gib einen Firmennamen ein.');
      return;
    }

    setLoading(true);
    try {
      const fullAddress = companyData.postal_code && companyData.city 
        ? `${companyData.address}, ${companyData.postal_code} ${companyData.city}`
        : companyData.address;
        
      await axios.post(`${API_URL}/api/companies`, {
        ...companyData,
        address: fullAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyCreated(true);
      setStep(2);
      toast.success('Firma erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Fehler beim Erstellen der Firma.');
    } finally {
      setLoading(false);
    }
  };

  // Download ZIP with folder structure
  const downloadFolderStructure = async () => {
    setLoading(true);
    try {
      const zip = new JSZip();
      
      // Create main folder
      const mainFolder = zip.folder('JaySmart_Dokumente');
      
      // Create subfolders with README files
      FOLDER_STRUCTURE.forEach(folder => {
        const subFolder = mainFolder.folder(folder.name);
        subFolder.file('README.txt', 
          `${folder.name}\n${'='.repeat(folder.name.length)}\n\n` +
          `${folder.description}\n\n` +
          `Lege hier alle relevanten Dokumente ab.\n` +
          `JaySmart erkennt und analysiert sie automatisch.\n\n` +
          `Unterstützte Formate: PDF, JPG, PNG\n`
        );
      });
      
      // Add main README
      mainFolder.file('LIES_MICH.txt',
        `JaySmart Dokumentenordner\n` +
        `========================\n\n` +
        `Dieser Ordner enthält 10 Kategorien für deine Dokumente:\n\n` +
        FOLDER_STRUCTURE.map((f, i) => `${i + 1}. ${f.name} - ${f.description}`).join('\n') +
        `\n\n` +
        `So funktioniert's:\n` +
        `-----------------\n` +
        `1. Entpacke diesen Ordner auf deinem Desktop\n` +
        `2. Wähle den Ordner "JaySmart_Dokumente" in der App aus\n` +
        `3. Lege neue Dokumente in den passenden Unterordner\n` +
        `4. JaySmart erkennt und analysiert sie automatisch!\n\n` +
        `Tipp: Die App sortiert neue Dokumente auch automatisch\n` +
        `in den richtigen Ordner, wenn du sie im Hauptordner ablegst.\n`
      );
      
      // Generate ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'JaySmart_Dokumente.zip');
      
      setZipDownloaded(true);
      toast.success('Ordnerstruktur heruntergeladen! Entpacke die ZIP-Datei auf deinem Desktop.');
    } catch (error) {
      console.error('Error creating ZIP:', error);
      toast.error('Fehler beim Erstellen der Ordnerstruktur.');
    } finally {
      setLoading(false);
    }
  };

  const selectFolder = async () => {
    try {
      if (!browserSupported) {
        toast.error('Dein Browser unterstützt diese Funktion nicht.');
        return;
      }

      const handle = await window.showDirectoryPicker({
        mode: 'readwrite'
      });
      
      setFolderHandle(handle);
      setFolderName(handle.name);
      localStorage.setItem('hotFolderName', handle.name);
      toast.success(`Ordner "${handle.name}" ausgewählt!`);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Folder selection error:', error);
        toast.error('Fehler bei der Ordnerauswahl.');
      }
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update AuthContext directly
      updateUser({ onboarding_completed: true });
      
      toast.success('Willkommen bei JaySmart!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Fehler beim Abschließen. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const skipFolderStep = () => {
    // Go to email setup step instead of completing
    setStep(3);
  };

  const saveEmailSettings = async () => {
    if (!emailSettings.smtp_email || !emailSettings.smtp_password) {
      toast.error('Bitte E-Mail und Passwort eingeben');
      return;
    }
    
    setSavingEmail(true);
    try {
      await axios.post(`${API_URL}/api/user/email-settings`, emailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-Mail-Einstellungen gespeichert!');
      await completeOnboarding();
    } catch (error) {
      toast.error('Fehler beim Speichern der E-Mail-Einstellungen');
      setSavingEmail(false);
    }
  };

  const skipEmailStep = async () => {
    await completeOnboarding();
  };

  const proceedToEmailStep = () => {
    setStep(3);
  };

  const steps = [
    { number: 1, title: 'Unternehmen', icon: Building2 },
    { number: 2, title: 'Dokumentenordner', icon: FolderOpen },
    { number: 3, title: 'E-Mail', icon: Mail }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            {steps.map((s, i) => (
              <div key={s.number} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  step >= s.number 
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white' 
                    : 'bg-white/10 text-white/50'
                }`}>
                  {step > s.number ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-24 h-1 mx-2 rounded ${step > s.number ? 'bg-violet-500' : 'bg-white/10'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-20 mt-2">
            {steps.map(s => (
              <span key={s.number} className={`text-sm ${step >= s.number ? 'text-white' : 'text-white/50'}`}>
                {s.title}
              </span>
            ))}
          </div>
        </div>

        {/* Content Card */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8"
        >
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-500/25">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Dein Unternehmen</h2>
                  <p className="text-white/60">Gib deine Unternehmensdaten ein, um loszulegen.</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1.5">Firmenname *</label>
                    <Input
                      value={companyData.name}
                      onChange={(e) => setCompanyData({...companyData, name: e.target.value})}
                      placeholder="z.B. Muster GmbH"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1.5">Rechtsform</label>
                      <select
                        value={companyData.legal_form}
                        onChange={(e) => setCompanyData({...companyData, legal_form: e.target.value})}
                        className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white text-sm"
                      >
                        <option value="" className="bg-slate-800">Auswählen...</option>
                        <option value="Einzelunternehmen" className="bg-slate-800">Einzelunternehmen</option>
                        <option value="GbR" className="bg-slate-800">GbR</option>
                        <option value="GmbH" className="bg-slate-800">GmbH</option>
                        <option value="UG" className="bg-slate-800">UG (haftungsbeschränkt)</option>
                        <option value="AG" className="bg-slate-800">AG</option>
                        <option value="Freiberufler" className="bg-slate-800">Freiberufler</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1.5">Branche</label>
                      <Input
                        value={companyData.industry}
                        onChange={(e) => setCompanyData({...companyData, industry: e.target.value})}
                        placeholder="z.B. IT-Dienstleistungen"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1.5">Straße & Hausnummer</label>
                    <Input
                      value={companyData.address}
                      onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
                      placeholder="Musterstraße 1"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1.5">PLZ</label>
                      <Input
                        value={companyData.postal_code}
                        onChange={(e) => setCompanyData({...companyData, postal_code: e.target.value})}
                        placeholder="12345"
                        maxLength={5}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-white/70 mb-1.5">Stadt</label>
                      <Input
                        value={companyData.city}
                        onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
                        placeholder="Berlin"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1.5">USt-IdNr. (optional)</label>
                    <Input
                      value={companyData.vat_id}
                      onChange={(e) => setCompanyData({...companyData, vat_id: e.target.value})}
                      placeholder="DE123456789"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <Button
                    onClick={handleCompanySubmit}
                    disabled={loading || !companyData.name}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-8 text-white"
                  >
                    {loading ? 'Wird gespeichert...' : 'Weiter'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
                    <FolderOpen className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Dokumentenordner einrichten</h2>
                  <p className="text-white/60">Lade die Ordnerstruktur herunter und wähle sie aus.</p>
                </div>

                {/* Step 1: Download ZIP */}
                <div className={`rounded-2xl p-6 border mb-4 transition-all ${
                  zipDownloaded 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : 'bg-white/5 border-white/10'
                }`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      zipDownloaded ? 'bg-emerald-500' : 'bg-violet-500/20'
                    }`}>
                      {zipDownloaded ? <Check className="w-5 h-5 text-white" /> : <span className="text-violet-400 font-bold">1</span>}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">Ordnerstruktur herunterladen</h3>
                      <p className="text-sm text-white/60 mb-3">
                        Lade die ZIP-Datei herunter und entpacke sie auf deinem Desktop. 
                        Sie enthält 10 vordefinierte Ordner für deine Dokumente.
                      </p>
                      
                      {/* Folder Preview */}
                      <div className="grid grid-cols-5 gap-2 mb-4">
                        {FOLDER_STRUCTURE.slice(0, 5).map(folder => (
                          <div key={folder.name} className="bg-white/5 rounded-lg p-2 text-center">
                            <FolderPlus className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                            <p className="text-[10px] text-white/60 truncate">{folder.name}</p>
                          </div>
                        ))}
                      </div>
                      
                      <Button
                        onClick={downloadFolderStructure}
                        disabled={loading}
                        variant={zipDownloaded ? "outline" : "default"}
                        className={zipDownloaded 
                          ? "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" 
                          : "bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
                        }
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {zipDownloaded ? 'Erneut herunterladen' : 'ZIP herunterladen'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Step 2: Select Folder */}
                <div className={`rounded-2xl p-6 border mb-4 transition-all ${
                  folderHandle 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : zipDownloaded ? 'bg-white/5 border-white/10' : 'bg-white/5 border-white/10 opacity-50'
                }`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      folderHandle ? 'bg-emerald-500' : 'bg-violet-500/20'
                    }`}>
                      {folderHandle ? <Check className="w-5 h-5 text-white" /> : <span className="text-violet-400 font-bold">2</span>}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">Ordner mit JaySmart verbinden</h3>
                      <p className="text-sm text-white/60 mb-3">
                        Wähle den entpackten "JaySmart_Dokumente" Ordner aus. 
                        Die App überwacht diesen Ordner und analysiert neue Dokumente automatisch.
                      </p>
                      
                      {!browserSupported ? (
                        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-amber-400 text-sm">Nur Chrome & Edge</p>
                              <p className="text-xs text-white/60 mt-1">
                                Die Ordner-Verbindung funktioniert nur mit Google Chrome oder Microsoft Edge.
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Chrome className="w-4 h-4 text-white/50" />
                                <span className="text-xs text-white/50">Bitte wechsle den Browser</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : folderHandle ? (
                        <div className="flex items-center gap-3 bg-emerald-500/20 rounded-xl p-3">
                          <FolderOpen className="w-5 h-5 text-emerald-400" />
                          <span className="text-emerald-400 font-medium">{folderName}</span>
                        </div>
                      ) : (
                        <Button
                          onClick={selectFolder}
                          disabled={!zipDownloaded}
                          className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                        >
                          <FolderOpen className="w-4 h-4 mr-2" />
                          Ordner auswählen
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/10 rounded-2xl p-4 border border-blue-500/30 mb-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-400 text-sm">100% lokal & sicher</p>
                      <p className="text-xs text-white/60 mt-1">
                        Deine Dokumente bleiben auf deinem Computer. Es werden nur die analysierten Metadaten 
                        (Betrag, Frist, Absender) in der App gespeichert – keine Dateien werden hochgeladen.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Desktop Sync Client Download */}
                <div className="bg-violet-500/10 rounded-2xl p-4 border border-violet-500/30 mb-6">
                  <div className="flex items-start gap-3">
                    <Monitor className="w-5 h-5 text-violet-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-violet-400 text-sm">Desktop Sync Client</p>
                      <p className="text-xs text-white/60 mt-1 mb-3">
                        Für automatische Synchronisation zwischen deinem Computer und JaySmart - funktioniert mit jedem Browser.
                      </p>
                      <Button
                        variant="ghost"
                        onClick={() => window.open(`${API_URL}/api/download/sync-client`, '_blank')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 h-auto bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 text-xs font-medium rounded-lg"
                        data-testid="download-sync-client-btn"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Sync Client herunterladen (Python)
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <Button
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  
                  <div className="flex items-center gap-3">
                    {browserSupported && !folderHandle && (
                      <Button
                        variant="ghost"
                        onClick={skipFolderStep}
                        disabled={loading}
                        className="text-white/50 hover:text-white hover:bg-white/10"
                        data-testid="skip-folder-btn"
                      >
                        Später einrichten
                      </Button>
                    )}
                    
                    {browserSupported ? (
                      <Button
                        onClick={proceedToEmailStep}
                        disabled={loading || !folderHandle}
                        className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 px-8 text-white"
                        data-testid="proceed-to-email-btn"
                      >
                        {loading ? 'Wird gestartet...' : 'Weiter'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={proceedToEmailStep}
                        disabled={loading}
                        className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-8 text-white"
                        data-testid="skip-folder-unsupported-btn"
                      >
                        {loading ? 'Wird gestartet...' : 'Weiter ohne Ordner'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Email Setup */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">E-Mail verbinden</h2>
                    <p className="text-white/60 text-sm">Versende E-Mails direkt aus JaySmart</p>
                  </div>
                </div>

                <p className="text-white/70 mb-6">
                  Verbinde deine E-Mail, um Fristverlängerungen und Rückfragen direkt aus der App zu versenden.
                </p>

                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/80 text-sm">SMTP-Server</Label>
                      <Input
                        value={emailSettings.smtp_server}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_server: e.target.value })}
                        placeholder="smtp.office365.com"
                        className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div>
                      <Label className="text-white/80 text-sm">Port</Label>
                      <Input
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: e.target.value })}
                        placeholder="587"
                        className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white/80 text-sm">E-Mail-Adresse</Label>
                    <Input
                      type="email"
                      value={emailSettings.smtp_email}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_email: e.target.value })}
                      placeholder="deine@email.de"
                      className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>

                  <div>
                    <Label className="text-white/80 text-sm">Passwort</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                        placeholder="••••••••"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Common SMTP servers */}
                <div className="bg-white/5 rounded-xl p-4 mb-6">
                  <p className="text-xs text-white/50 mb-2">Häufige E-Mail-Anbieter:</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setEmailSettings({ ...emailSettings, smtp_server: 'smtp.office365.com', smtp_port: '587' })}
                      className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full text-white/70"
                    >
                      Microsoft 365 / GoDaddy
                    </button>
                    <button
                      onClick={() => setEmailSettings({ ...emailSettings, smtp_server: 'smtp.gmail.com', smtp_port: '587' })}
                      className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full text-white/70"
                    >
                      Gmail
                    </button>
                    <button
                      onClick={() => setEmailSettings({ ...emailSettings, smtp_server: 'smtp.ionos.de', smtp_port: '587' })}
                      className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full text-white/70"
                    >
                      IONOS / 1&1
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <Button
                    variant="ghost"
                    onClick={() => setStep(2)}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      onClick={skipEmailStep}
                      disabled={loading || savingEmail}
                      className="text-white/50 hover:text-white hover:bg-white/10"
                    >
                      Später einrichten
                    </Button>
                    
                    <Button
                      onClick={saveEmailSettings}
                      disabled={savingEmail || !emailSettings.smtp_email || !emailSettings.smtp_password}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 px-8 text-white"
                    >
                      {savingEmail ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Speichern...
                        </>
                      ) : (
                        <>
                          Speichern & Loslegen
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <p className="text-center text-white/40 text-sm mt-6">
          Schritt {step} von {steps.length}
        </p>
      </div>
    </div>
  );
};

export default Onboarding;
